import React, { memo, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { useSaveCareerLessonNotes } from 'core/hooks/CareerCenter';

/** Idle → typing schedules a save after this quiet period (spec 0020 Фаза B). */
const AUTOSAVE_DEBOUNCE_MS = 800;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface ICareerLessonNotes {
  lessonId: string;
  /** Seed value from `myNotes`. The panel owns its state after mount, so the
   *  parent should remount it per lesson (`key={lesson.id}`) to re-seed. */
  initialNotes: string;
}

/**
 * Personal, student-only notes for a career lesson.
 *
 * Debounced autosave (~800ms of quiet) via `saveCareerLessonNotes`, with a
 * subtle «Сохранение…» / «Сохранено» indicator. The text is PRIVATE — the
 * backend never exposes it to staff (spec 0020 §3.3) — and the label says so.
 *
 * The panel is the single owner of the text after mount: it seeds once from
 * `initialNotes`, so a `cache-and-network` refetch of the lesson can't clobber
 * in-flight edits. A pending edit is flushed on unmount so a quick navigation
 * away doesn't drop the last keystrokes.
 */
export const CareerLessonNotes = memo(({ lessonId, initialNotes }: ICareerLessonNotes) => {
  const { t } = useTranslation();
  const { saveNotes } = useSaveCareerLessonNotes();

  const [value, setValue] = useState(initialNotes);
  const [savedValue, setSavedValue] = useState(initialNotes);
  const [status, setStatus] = useState<SaveStatus>('idle');

  // Mirror the latest value/savedValue for the unmount flush below.
  const latestRef = useRef({ value: initialNotes, savedValue: initialNotes });
  latestRef.current = { value, savedValue };

  // Debounced autosave: every keystroke resets the timer; nothing runs while the
  // text already matches what the server has (`value === savedValue`).
  useEffect(() => {
    if (value === savedValue) {
      return undefined;
    }

    setStatus('saving');
    const handle = setTimeout(async () => {
      try {
        const result = await saveNotes(lessonId, value);
        if (result.success) {
          setSavedValue(value);
          setStatus('saved');
        } else {
          setStatus('error');
        }
      } catch {
        setStatus('error');
      }
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [value, savedValue, lessonId, saveNotes]);

  // Flush a pending edit if the student navigates away before the debounce fires.
  useEffect(
    () => () => {
      const { value: pending, savedValue: persisted } = latestRef.current;
      if (pending !== persisted) {
        // Fire-and-forget flush; a caught rejection avoids an unhandled promise.
        saveNotes(lessonId, pending).catch(() => undefined);
      }
    },
    [lessonId, saveNotes],
  );

  return (
    <Panel>
      <PanelHead>
        <PanelTitle>{t('careerCenter.lesson.notes.title')}</PanelTitle>
        <StatusText data-status={status}>
          {status === 'saving'
            ? t('careerCenter.lesson.notes.saving')
            : status === 'saved'
            ? t('careerCenter.lesson.notes.saved')
            : status === 'error'
            ? t('careerCenter.lesson.notes.error')
            : ''}
        </StatusText>
      </PanelHead>

      <PanelHint>{t('careerCenter.lesson.notes.hint')}</PanelHint>

      <NotesArea
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={t('careerCenter.lesson.notes.placeholder')}
        rows={5}
        aria-label={t('careerCenter.lesson.notes.title')}
      />
    </Panel>
  );
});

const Panel = styled.div`
  width: 100%;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${Colors.system_gray};
`;

const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const PanelTitle = styled.h3`
  margin: 0;
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.bold })}
`;

const StatusText = styled.span`
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};

  &[data-status='error'] {
    color: ${Colors.signal_red};
  }

  &[data-status='saved'] {
    color: ${Colors.marker_green};
  }
`;

const PanelHint = styled.p`
  margin: 4px 0 12px 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const NotesArea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;
  background-color: ${Colors.white};
  color: ${Colors.black};
  resize: vertical;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}

  &:focus {
    outline: none;
    border-color: ${Colors.main};
  }

  &::placeholder {
    color: ${Colors.gray_dark};
  }
`;
